import type { ApiResponse } from '@bcube/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { LocalAddress } from '@/context/create-account-flow';
import { useDebounceFn } from '@/hooks/use-debounce';
import { useFormError } from '@/hooks/use-form-error';
import { useToast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

const addressSchema = z.object({
  zip_code: z.string().min(8, 'CEP obrigatório'),
  street: z.string().min(1, 'Rua obrigatória'),
  number: z.string().min(1, 'Número obrigatório'),
  complement: z.string().optional(),
  district: z.string().min(1, 'Bairro obrigatório'),
  city: z.string().min(1, 'Cidade obrigatória'),
  state: z.string().min(2, 'Estado obrigatório'),
  reference_point: z.string().optional(),
  type: z.array(z.string()).min(1, 'Selecione ao menos um tipo'),
});

type AddressFormData = z.infer<typeof addressSchema>;
type CepAddress = Pick<AddressFormData, 'street' | 'district' | 'city' | 'state' | 'complement'>;

const ADDRESS_TYPES = ['Comercial', 'Entrega', 'Faturamento', 'Residencial'];

interface AddressFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: LocalAddress) => void;
  defaultValues?: Partial<AddressFormData>;
  address?: LocalAddress;
  mode?: 'create' | 'edit';
  duplicateMode?: boolean;
}

export function AddressFormDialog({
  open,
  onClose,
  onSubmit,
  defaultValues,
  address,
  mode = 'create',
  duplicateMode = false,
}: AddressFormDialogProps) {
  const { formError, showFormError, clearFormError } = useFormError(open);
  const { toast } = useToast();
  const [isLoadingCep, setIsLoadingCep] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<AddressFormData>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      zip_code: '',
      street: '',
      number: '',
      complement: '',
      district: '',
      city: '',
      state: '',
      reference_point: '',
      type: [],
      ...defaultValues,
    },
  });

  const selectedTypes = watch('type') || [];

  useEffect(() => {
    if (open) {
      if (mode === 'edit' && address) {
        reset({
          zip_code: address.zip_code || '',
          street: address.street || '',
          number: address.number || '',
          complement: address.complement || '',
          district: address.district || '',
          city: address.city || '',
          state: address.state || '',
          reference_point: address.reference_point || '',
          type: address.type || [],
        });
      } else {
        reset({
          zip_code: defaultValues?.zip_code || '',
          street: defaultValues?.street || '',
          number: defaultValues?.number || '',
          complement: defaultValues?.complement || '',
          district: defaultValues?.district || '',
          city: defaultValues?.city || '',
          state: defaultValues?.state || '',
          reference_point: defaultValues?.reference_point || '',
          type: defaultValues?.type || [],
        });
      }
    }
  }, [open, address, mode, defaultValues, reset]);

  const handleCepLookup = async (cep: string) => {
    const cleanCep = cep.replace(/\D/g, '');
    if (cleanCep.length !== 8) return;

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;

    setIsLoadingCep(true);
    try {
      const { data: response } = await api.get<ApiResponse<CepAddress>>(
        `/integrations/viacep/${cleanCep}`,
        {
          signal: controller.signal,
        },
      );
      const data = response.data;
      if (response.success && data) {
        setValue('street', data.street || '');
        setValue('district', data.district || '');
        setValue('city', data.city || '');
        setValue('state', data.state || '');
        setValue('complement', data.complement || '');
      }
    } catch (error) {
      if (controller.signal.aborted) return;
      console.warn('Failed to lookup CEP:', error);
      toast({
        title: 'Erro ao buscar CEP',
        description: 'Não foi possível buscar os dados do CEP. Verifique o CEP informado.',
        variant: 'destructive',
      });
    } finally {
      if (!controller.signal.aborted) {
        setIsLoadingCep(false);
      }
    }
  };

  const debouncedCepLookup = useDebounceFn(handleCepLookup, 300);

  const handleTypeChange = (type: string, checked: boolean) => {
    if (checked) {
      setValue('type', [...selectedTypes, type]);
    } else {
      setValue(
        'type',
        selectedTypes.filter((t) => t !== type),
      );
    }
  };

  const onFormSubmit = async (data: AddressFormData) => {
    try {
      const addressData: LocalAddress = {
        id: duplicateMode ? crypto.randomUUID() : address?.id || crypto.randomUUID(),
        ...data,
        zip_code: data.zip_code.replace(/\D/g, ''),
      };
      await onSubmit(addressData);
      onClose();
    } catch (error) {
      showFormError(error);
    }
  };

  const formatCep = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 5) return digits;
    return `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>
            {duplicateMode
              ? 'Duplicar Endereço'
              : mode === 'edit'
                ? 'Editar Endereço'
                : 'Novo Endereço'}
          </DialogTitle>
        </DialogHeader>

        <form
          noValidate
          onSubmitCapture={clearFormError}
          onSubmit={handleSubmit(onFormSubmit)}
          className="space-y-4"
        >
          {/* CEP */}
          <div className="space-y-1.5">
            <Label htmlFor="zip_code">CEP *</Label>
            <Controller
              name="zip_code"
              control={control}
              render={({ field }) => (
                <Input
                  {...field}
                  id="zip_code"
                  placeholder="00000-000"
                  onChange={(e) => {
                    const formatted = formatCep(e.target.value);
                    field.onChange(formatted);
                    if (formatted.replace(/\D/g, '').length === 8) {
                      debouncedCepLookup(formatted);
                    }
                  }}
                  disabled={isLoadingCep || duplicateMode}
                />
              )}
            />
            {errors.zip_code && <p className="text-xs text-red-500">{errors.zip_code.message}</p>}
          </div>

          {/* Cidade / Estado - relacionados */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 space-y-1.5">
              <Label htmlFor="city">Cidade *</Label>
              <Input
                id="city"
                {...register('city')}
                placeholder="Cidade"
                disabled={isLoadingCep || duplicateMode}
              />
              {errors.city && <p className="text-xs text-red-500">{errors.city.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="state">UF *</Label>
              <Input
                id="state"
                {...register('state')}
                placeholder="SP"
                disabled={isLoadingCep || duplicateMode}
              />
              {errors.state && <p className="text-xs text-red-500">{errors.state.message}</p>}
            </div>
          </div>

          {/* Bairro */}
          <div className="space-y-1.5">
            <Label htmlFor="district">Bairro *</Label>
            <Input
              id="district"
              {...register('district')}
              placeholder="Bairro"
              disabled={isLoadingCep || duplicateMode}
            />
            {errors.district && <p className="text-xs text-red-500">{errors.district.message}</p>}
          </div>

          {/* Rua / Numero - relacionados */}
          <div className="grid grid-cols-4 gap-3">
            <div className="col-span-3 space-y-1.5">
              <Label htmlFor="street">Rua *</Label>
              <Input
                id="street"
                {...register('street')}
                placeholder="Rua / Avenida"
                disabled={isLoadingCep || duplicateMode}
              />
              {errors.street && <p className="text-xs text-red-500">{errors.street.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="number">Nº *</Label>
              <Input
                id="number"
                {...register('number')}
                placeholder="123"
                disabled={duplicateMode}
              />
              {errors.number && <p className="text-xs text-red-500">{errors.number.message}</p>}
            </div>
          </div>

          {/* Complemento */}
          <div className="space-y-1.5">
            <Label htmlFor="complement">Complemento</Label>
            <Input
              id="complement"
              {...register('complement')}
              placeholder="Apto, Bloco, Sala..."
              disabled={duplicateMode}
            />
          </div>

          {/* Referencia */}
          <div className="space-y-1.5">
            <Label htmlFor="reference_point">Ponto de Referência</Label>
            <Input
              id="reference_point"
              {...register('reference_point')}
              placeholder="Próximo a..."
              disabled={duplicateMode}
            />
          </div>

          {/* Tipo de Endereço */}
          <div className="space-y-2">
            <Label>Tipo de Endereço *</Label>
            <div className="flex flex-wrap gap-4">
              {ADDRESS_TYPES.map((type) => (
                <div key={type} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={selectedTypes.includes(type)}
                    onCheckedChange={(checked) => handleTypeChange(type, checked as boolean)}
                  />
                  <span className="text-sm">{type}</span>
                </div>
              ))}
            </div>
            {errors.type && <p className="text-xs text-red-500">{errors.type.message}</p>}
          </div>

          <FormError error={formError} onDismiss={clearFormError} />
          <DialogFooter className="pt-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-black text-white hover:bg-gray-800"
              disabled={isSubmitting || isLoadingCep}
            >
              {isSubmitting
                ? 'Salvando...'
                : duplicateMode
                  ? 'Duplicar'
                  : mode === 'edit'
                    ? 'Salvar'
                    : 'Adicionar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
